import get from 'lodash.get';
import groupBy from 'lodash.groupby';
import sortBy from 'lodash.sortby';
import flatten from 'lodash.flatten';
import values from 'lodash.values';
import { GRADE, PERIOD } from '../../constants/enrollment';
import { getSortedGrades, sortGrades } from '../utils/prepareFormsData';
import { CS_MEASURES } from '../../constants/assessments';

const FORM_UID_KEY = 'formUid';
const PROBE_ID_KEY = 'probeId';

const sortResultsByDate = (a, b) => new Date(b.clientModDate) - new Date(a.clientModDate);
const sortResultsByCreateDate = (a, b) =>
    new Date(b.clientCreateDate) - new Date(a.clientCreateDate);

/**
 * Return grade for available forms by selected grade that meats requirements:
 * * If form exists for selected grade returns selected grade
 * * If no forms available for this grade calculate closest grade with available forms to selected grade.
 * We need this only for cases when burst recommends out of grade measure for student.
 * Student grade K with ORF required:
 *      First grade will be returned
 * Student grade 6 with NWF:
 *      Third grade will be returned
 * etc...
 * @param {Object} forms
 * @param {String} studentGrade
 * @returns {String} available grade
 */
export const getClosestAvailableFormGrade = (forms, studentGrade) => {
    const availableGrades = getSortedGrades(forms);
    if (availableGrades.includes(studentGrade)) {
        return studentGrade;
    }
    const grades = sortGrades(Object.values(GRADE));
    const firstGrade = availableGrades[0];
    const lastGrade = availableGrades[availableGrades.length - 1];
    return grades.indexOf(studentGrade) < grades.indexOf(firstGrade) ? firstGrade : lastGrade;
};

/**
 * Get list of available forms for selected grade. If no forms available for this grade
 * calculate first or last grade for forms and get forms for that grade.
 * We need this only for cases when burst recommends out of grade measure for student.
 * Student grade K with ORF required:
 *      First available grade forms (1st grade forms) will be selected
 * Student grade 6 with NWF:
 *      Last available grade forms (3rd grade forms) will be selected
 * etc...
 * @param {String} grade to get forms for
 * @param {Object} formsMap forms data grouped by form UID
 * @param {String} measure to get from for
 * @returns {*|Array|*}
 */
const getAvailableForms = (formsMap, grade, measure) => {
    const measureForms = Object.keys(formsMap)
        .filter(
            formUid =>
                formsMap[formUid].measure === measure && formsMap[formUid].period === PERIOD.PM,
        )
        .map(formUid => formsMap[formUid]);
    const measureFormsByGrade = groupBy(measureForms, 'grade');
    const closestGrade = getClosestAvailableFormGrade(measureFormsByGrade, grade);
    return measureFormsByGrade[closestGrade] || [];
};

const getLatestAssessedFormUid = results => {
    const [firstResult] = results.sort(sortResultsByCreateDate).slice(0, 1);
    return get(firstResult, FORM_UID_KEY);
};

/**
 * Filters out the results assessed over BM/PM measure forms.
 * @param {Array<Object>} forms BM/PM forms for grade-measure
 * @param {Array<Object>} results student results for a single measure.
 * @returns {Array<Object>} filtered measure results.
 * f. e.
 * const forms = [
 *      {
 *          uid: '42656E63686D61726B204F52462E352E456E647C7C7C31',
 *          grade: '02',
 *          ...
 *      },
 *      {
 *          uid: '42656E63686D61726B204F52462E312E4D6964646C657C7C7C31',
 *          grade: '02',
 *          ...
 *      },
 *      ...
 * ];
 *
 * const results = [
 *      {
 *          grade: '01',
 *          formUid: '42656E63686D61726B204F52462E352E456E647C7C7C31',
 *          ...
 *      }
 * ];
 *
 * const filteredResults = getResultsOverForms(forms, results);
 * filteredResults = [
 *      {
 *          grade: '01',
 *          formUid: '42656E63686D61726B204F52462E352E456E647C7C7C31',
 *          ...
 *      }
 * ];
 */
const getResultsOverForms = (forms, results) =>
    results.filter(result => forms.some(form => form.uid === result.formUid));

export const getNextFormBySequence = (formUid, measureForms) => {
    const assessedForm = measureForms.find(form => form.uid === formUid);
    const filteredForms = measureForms
        .filter(
            // Dropping forms that have same sequence as last assessed form.
            // This have been done to prevent situation when user assessed
            // only Downloaded PDF form, and we should exclude this form
            // from sequence to get correct next form
            form => form.sequence !== get(assessedForm, 'sequence') || form.uid === formUid,
        )
        // Remove forms with duplicated sequences if last assess form is not Downloaded PDF form
        .filter((form, i, arr) => arr.findIndex(el => el.sequence === form.sequence) === i);

    // Sort form array by growing sequence
    const formsInSequenceOrder = sortBy(filteredForms, form => parseInt(form.sequence, 10));

    const assessedFormIndex = formsInSequenceOrder.findIndex(form => form.uid === formUid);
    // Getting last form: (19 + 1) % 20 = 20 % 20 = 0 (reset to 0 in case of overflow)
    return formsInSequenceOrder[(assessedFormIndex + 1) % formsInSequenceOrder.length];
};

/**
 * Looks for the latest result over provided measure PM forms and picks form next to latest assessed.
 * @param {Array<Object>} measureForms PM forms for grade-measure
 * @param {Array<Object>} measureResults student results for a single measure through all TOYs including invalidated.
 * @returns {Object} form to be recommended to assess next.
 */
export const getNextForm = (measureForms, measureResults) => {
    const latestFormUid = getLatestAssessedFormUid(
        getResultsOverForms(measureForms, measureResults),
    );
    return getNextFormBySequence(latestFormUid, measureForms);
};

/**
 * Returns next PM form ID for recommended measures
 * @param {String} grade
 * @param {Object} results grouped by periods
 * @param {Object} formsMap forms data grouped by form UID
 * @param {Array} measures
 * @returns {*} object with next form uid data:
 * f. e.
 * {
 *     uid: '50726F6772657373204D6F6E69746F72696E67204E57462E4B2E317C7C7C31'
 * }
 */
export const getD8NextForm = (grade, results, formsMap, measures) => {
    const resultsByMeasure = groupBy(flatten(values(results)), 'measure');
    return measures.reduce((acc, measure) => {
        const nextForm = getNextForm(
            getAvailableForms(formsMap, grade, measure),
            get(resultsByMeasure, measure, []),
        );
        return {
            ...acc,
            [measure]: get(nextForm, 'uid', ''),
        };
    }, {});
};

/**
 * Returns next PM form ID for recommended measures
 * @param {String} grade
 * @param {Object} results grouped by periods
 * @param {Object} formsMap forms data grouped by form UID
 * @param {Array} measures
 * @returns {*} object with next form uid data:
 * f. e.
 * {
 *     uid: '50726F6772657373204D6F6E69746F72696E67204E57462E4B2E317C7C7C31'
 * }
 */
export const getLecturaNextForm = (grade, results, formsMap, measures) => {
    const resultsByMeasure = groupBy(flatten(values(results)), 'measure');
    return measures.reduce((acc, measure) => {
        const nextForm = getNextForm(
            getAvailableForms(formsMap, grade, measure),
            get(resultsByMeasure, measure, []),
        );
        return {
            ...acc,
            [measure]: get(nextForm, 'uid', ''),
        };
    }, {});
};

/**
 * Returns next PM form ID for burst measures
 * @param {String} grade
 * @param {Object} resultGroups grouped by periods
 * @param {Object} formsMap forms data grouped by form UID
 * @param {Array} measures
 * @returns {*} object with next form uid data:
 * f. e.
 * {
 *     uid: '50726F6772657373204D6F6E69746F72696E67204E57462E4B2E317C7C7C31'
 * }
 */
export const getBurstNextForm = (grade, resultGroups, formsMap, measures) => {
    const results = get(resultGroups, `${PERIOD.PM}`, []).reduce((allResults, resultGroup) => {
        return allResults.concat(resultGroup.results);
    }, []);
    const resultsByMeasure = groupBy(results, 'measure');
    return measures.reduce((acc, measure) => {
        const sortedPMResults = get(resultsByMeasure, `${measure}`, [])
            .filter(result => get(formsMap, `${get(result, `${PROBE_ID_KEY}`)}.grade`) === grade)
            .sort(sortResultsByDate);
        const lastFormUid = get(sortedPMResults, `[0][${PROBE_ID_KEY}]`);
        const lastFormNumber = get(formsMap, `${lastFormUid}.sequence`, -1);
        const measureForms = getAvailableForms(formsMap, grade, measure);
        const maxFormNumber = Math.max(...measureForms.map(form => form.sequence));
        const seqIncrement = CS_MEASURES.includes(measure) && lastFormNumber >= 0 ? 2 : 1;
        const nextUnusedForm = measureForms.find(
            form => form.sequence === (lastFormNumber + seqIncrement) % (maxFormNumber + 1),
        );
        return {
            ...acc,
            [measure]: get(nextUnusedForm, 'uid', ''),
        };
    }, {});
};
